// fileUpload.js - Handles file uploads and processing for the AI tutor
import { uploadFileToS3, uploadImageToS3 } from './S3Utils';
import heic2any from 'heic2any';

/**
 * Compress an image to reduce its file size for Bedrock API
 * @param {File|Blob} imageFile - The image to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed image file
 */
async function compressImage(imageFile, options = {}) {
  const { 
    maxWidth = 1600, 
    maxHeight = 1600, 
    quality = 0.8,
    targetType = 'image/jpeg',
    maxSizeKB = 1000 // 1MB target max size
  } = options;
  
  try {
    console.log('Compressing image. Original size:', Math.round(imageFile.size / 1024), 'KB');
    console.time('🔄 Image Compression Time');
    
    // Create an image element to load the file
    const img = new Image();
    const imageUrl = URL.createObjectURL(imageFile);
    
    // Wait for image to load
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imageUrl;
    });
    
    // Release the object URL
    URL.revokeObjectURL(imageUrl);
    
    // Calculate new dimensions while maintaining aspect ratio
    let width = img.width;
    let height = img.height;
    
    console.log('Original dimensions:', width, 'x', height);
    
    // Scale down the image if it exceeds the max dimensions
    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width = Math.floor(width * ratio);
      height = Math.floor(height * ratio);
      console.log('Resized dimensions:', width, 'x', height);
    }
    
    // Create a canvas to draw the resized image
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    
    // Draw the image on the canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    
    // Convert canvas to blob with the specified quality
    let compressedBlob = await new Promise(resolve => {
      canvas.toBlob(resolve, targetType, quality);
    });
    
    // If the image is still too large, reduce quality further
    let currentQuality = quality;
    const minQuality = 0.5;
    const qualityStep = 0.1;
    
    while (compressedBlob.size > maxSizeKB * 1024 && currentQuality > minQuality) {
      currentQuality -= qualityStep;
      console.log(`Image still too large (${Math.round(compressedBlob.size / 1024)}KB), reducing quality to ${currentQuality.toFixed(1)}`);
      
      compressedBlob = await new Promise(resolve => {
        canvas.toBlob(resolve, targetType, currentQuality);
      });
    }
    
    // Create a new File object with the compressed blob
    const fileExtension = targetType === 'image/png' ? '.png' : '.jpg';
    const fileName = imageFile.name.replace(/\.[^/.]+$/, '') + fileExtension;
    
    const compressedFile = new File([compressedBlob], fileName, { 
      type: targetType 
    });
    
    console.timeEnd('🔄 Image Compression Time');
    console.log('Compression complete:', 
      'Original:', Math.round(imageFile.size / 1024), 'KB',
      '→ Compressed:', Math.round(compressedFile.size / 1024), 'KB',
      `(${Math.round((compressedFile.size / imageFile.size) * 100)}% of original)`
    );
    
    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    throw new Error(`Image compression failed: ${error.message}`);
  }
}

/**
 * Convert HEIC image to PNG format
 * @param {File|Blob} heicFile - The HEIC file to convert
 * @returns {Promise<Blob>} - Converted PNG blob
 */
async function convertHeicToPng(heicFile) {
  try {
    console.log('Converting HEIC image to PNG format...');
    console.time('🔄 HEIC Conversion Time');
    
    // Use heic2any to convert HEIC to PNG
    const convertedBlob = await heic2any({
      blob: heicFile,
      toType: 'image/jpeg', // Use JPEG for better compression
      quality: 0.85        // Lower initial quality to reduce file size
    });
    
    console.timeEnd('🔄 HEIC Conversion Time');
    
    // heic2any may return an array if the HEIC contains multiple images
    // We'll just use the first one in that case
    const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    
    console.log('HEIC conversion complete. Original size:', heicFile.size, 'Converted size:', jpegBlob.size);
    
    // Create a new File object with the same name but .jpg extension
    const fileName = heicFile.name.replace(/\.heic$/i, '.jpg');
    const convertedFile = new File([jpegBlob], fileName, { type: 'image/jpeg' });
    
    // If the converted file is too large, compress it
    if (convertedFile.size > 1500 * 1024) { // If larger than 1.5MB
      console.log('Converted file is large, applying additional compression...');
      return await compressImage(convertedFile, {
        maxWidth: 2048,
        maxHeight: 2048,
        quality: 0.75,
        targetType: 'image/jpeg',
        maxSizeKB: 1000 // Target 1MB max size for Bedrock
      });
    }
    
    return convertedFile;
  } catch (error) {
    console.error('Error converting HEIC to PNG:', error);
    throw new Error(`HEIC conversion failed: ${error.message}`);
  }
}

/**
 * Get text content from an image using Bedrock vision model
 * @param {File} imageFile - The image file to process
 * @param {string} imageUrl - URL of the already uploaded image
 * @returns {Promise<string>} - Extracted text content
 */
async function getImageTextContent(imageFile, imageUrl, userId, bedrockClient) {
  try {
    if (!bedrockClient) {
      console.error('Bedrock client reference not found');
      return "Image uploaded but text extraction failed: Bedrock client not available";
    }
    
    console.log('Calling Bedrock vision model to extract text from image:', imageUrl);
    
    // Use a try-catch within the function to better handle errors
    console.time('🖼️ Bedrock Image Text Extraction Time');
    
    // For Claude 3.5 Haiku, make sure we're passing the image in the correct format
    // Some models require base64 while others require URLs
    // Try with URL format first (this seems to be what your current code attempts)
    const textContent = await bedrockClient.describeImage(imageUrl, userId);
    
    console.timeEnd('🖼️ Bedrock Image Text Extraction Time');
    console.log('Extracted text content:', textContent);
    
    return textContent || "No text content detected in the image.";
  } catch (error) {
    console.error('Error extracting text from image:', error);
    return "Image uploaded but text extraction failed: " + error.message;
  }
}

/**
 * Upload a file to S3 and process it to extract text content
 * @param {File} file - The file to upload and process
 * @param {Object} options - Optional parameters including userId and sessionId
 * @returns {Promise<Object>} - Result object with file info and extracted text
 */
export async function uploadAndProcessFile(file, options = {}) {
  try {
    if (!file) {
      throw new Error('No file provided');
    }

    const { userId, sessionId: inputSessionId, bedrockClient, dynamoUtils } = options;

    if (!userId) {
      throw new Error('User ID not available');
    }

    if (!bedrockClient) {
      throw new Error('Bedrock client not available. Please ensure you are logged in.');
    }

    let sessionId = inputSessionId || `upload_session_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
    
    // Check if the file is HEIC and convert if necessary
    let processedFile = file;
    let originalFormat = file.type;
    let compressionApplied = false;
    
    if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
      try {
        console.log('Detected HEIC image, converting to JPEG before processing...');
        processedFile = await convertHeicToPng(file);
        compressionApplied = true;
        console.log('HEIC conversion successful. Proceeding with converted file:', processedFile.name);
      } catch (conversionError) {
        console.error('HEIC conversion failed:', conversionError);
        throw new Error('Failed to convert HEIC image. Please try a different format.');
      }
    } else if (file.type.startsWith('image/') && file.size > 1500 * 1024) {
      // For non-HEIC images that are still large, compress them too
      try {
        console.log('Image file is large, applying compression...');
        processedFile = await compressImage(file, {
          maxWidth: 2048,
          maxHeight: 2048,
          quality: 0.8,
          targetType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
          maxSizeKB: 1000 // Target 1MB
        });
        compressionApplied = true;
        console.log('Image compression successful. Proceeding with compressed file:', processedFile.name);
      } catch (compressionError) {
        console.error('Image compression failed:', compressionError);
        // Continue with original file if compression fails
        console.log('Proceeding with original uncompressed file');
      }
    }
    
    // Create file reader for image preview (client-side only)
    const imagePreviewUrl = processedFile.type.startsWith('image/') 
      ? await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(processedFile);
        })
      : null;
    
    // Upload file to S3
    console.log(`Uploading file ${processedFile.name} to S3 for session ${sessionId}...`);
    const fileUrl = await uploadFileToS3(processedFile, { sessionId, userId });
    
    if (!fileUrl) {
      throw new Error('Failed to upload file to S3');
    }
    
    console.log(`File uploaded successfully to: ${fileUrl}`);
    
    // Extract text content based on file type
    let textContent = '';
    
    if (processedFile.type.startsWith('image/')) {
      textContent = await getImageTextContent(processedFile, fileUrl, userId, bedrockClient);
    } else {
      // For now, we're only handling images as specified in the requirements
      textContent = `File uploaded: ${processedFile.name}`;
    }
    
    // Return all the necessary information
    return {
      fileName: file.name, // Keep the original filename for display
      fileType: originalFormat, // Keep track of the original format
      convertedType: processedFile.type !== file.type ? processedFile.type : undefined,
      compressionApplied,
      fileUrl: fileUrl,
      imagePreviewUrl,
      text: textContent
    };
  } catch (error) {
    console.error('Error in uploadAndProcessFile:', error);
    throw error;
  }
}